import React, { useState } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';

import { BUTTON_THEMES, CARD_TYPES } from '../../../shared/constants';
import { IContentfulLinkWithModal } from '../../../types/shared';

import PrimaryButton from '../../atoms/primary-button/PrimaryButton';
import SecondaryButton from '../../atoms/secondary-button/SecondaryButton';

import { DEQ_TYPES } from '../../../utils/deq';
import { getCtaLink, ReactMarkdownExtended } from '../../../utils/generic';

import mq from '../../../styles/breakpoints';
import buttonStyles, { CtaWrapper } from '../../../styles/buttonStyles';
import { Body, H2 } from '../../../styles/generic';
import Spacings from '../../../styles/Spacings';

import { Alignments } from '../../../tokens';
import Modal from '../../organisms/modal/Modal';
import Media from '../media/Media';
import { SimpleContent } from '../simple-content/SimpleContent';

import { AnchorCardProps, StyledCardProps } from './AnchorCard.types';

const AnchorCard = ({
  id,
  index,
  alignment,
  title,
  image,
  primaryLink,
  secondaryLink,
  children,
  videoLink,
}: AnchorCardProps) => {
  const cardAlignment =
    alignment || (index % 2 === 0 ? Alignments.RIGHT : Alignments.LEFT);
  const [showModal, setShowModalHandler] = useState(false);
  const [activeCtaLink, setActiveCtaLink] =
    useState<IContentfulLinkWithModal>();

  const handleOpenModal = (link: IContentfulLinkWithModal) => {
    setShowModalHandler(true);
    setActiveCtaLink(link);
  };

  const ctaPrimaryLink =
    primaryLink && !primaryLink.dialogueModal
      ? getCtaLink(primaryLink)
      : undefined;

  const ctaSecondaryLink =
    secondaryLink && !secondaryLink.dialogueModal
      ? getCtaLink(secondaryLink)
      : undefined;

  const renderButtons = () => (
    <CtaWrapper>
      {primaryLink && (
        <StyledPrimaryButton
          showModal={!!primaryLink?.dialogueModal}
          onButtonClick={() =>
            primaryLink.dialogueModal && handleOpenModal(primaryLink)
          }
          type={primaryLink?.type}
          label={primaryLink.label || primaryLink.name}
          buttonTheme={primaryLink.theme || BUTTON_THEMES.default}
          to={ctaPrimaryLink as string}
          iconPosition={primaryLink?.iconPosition}
          icon={primaryLink?.icon}
          deqComponentTitle={title}
          deqCustomAttributes={{
            type: DEQ_TYPES.textLink,
          }}
        />
      )}
      {secondaryLink && (
        <StyledSecondaryButton
          showModal={!!secondaryLink?.dialogueModal}
          onButtonClick={() =>
            secondaryLink.dialogueModal && handleOpenModal(secondaryLink)
          }
          type={secondaryLink?.type}
          label={secondaryLink.label || secondaryLink.name}
          buttonTheme={secondaryLink.theme || BUTTON_THEMES.default}
          to={ctaSecondaryLink as string}
          iconPosition={secondaryLink?.iconPosition}
          icon={secondaryLink?.icon}
          deqComponentTitle={title}
          deqCustomAttributes={{
            type: DEQ_TYPES.textLink,
          }}
        />
      )}
    </CtaWrapper>
  );

  return (
    <>
      <Card
        id={id || CARD_TYPES.anchor}
        alignment={cardAlignment}
        key={title + index}
      >
        <VisualContainer>
          {videoLink ? (
            <Media src={videoLink} />
          ) : (
            <GatsbyImage
              alt=""
              image={{
                ...image.gatsbyImageData,
                layout: 'fullWidth',
              }}
              style={{ width: '100%', height: '100%' }}
              imgStyle={{
                objectFit: 'cover',
              }}
            />
          )}
        </VisualContainer>
        <TextContainer>
          <StyledH2>{title}</StyledH2>
          {children && <Body>{children}</Body>}
          {(primaryLink || secondaryLink) && renderButtons()}
        </TextContainer>
      </Card>
      <Modal
        show={showModal}
        onRequestCloseHandler={() => setShowModalHandler(!showModal)}
      >
        {activeCtaLink?.dialogueModal && (
          <SimpleContent
            setShowModal={(event) => {
              setShowModalHandler(event);
            }}
            title={activeCtaLink.dialogueModal.title}
            buttonLabel="Close"
            primaryLink={activeCtaLink?.dialogueModal.primaryLink}
          >
            <ReactMarkdownExtended componentType={CARD_TYPES.anchor}>
              {
                activeCtaLink.dialogueModal.body.childMarkdownRemark
                  .rawMarkdownBody
              }
            </ReactMarkdownExtended>
          </SimpleContent>
        )}
      </Modal>
    </>
  );
};

export default AnchorCard;

const Card = styled.div<StyledCardProps>`
  display: flex;
  flex-direction: column;

  ${mq.from.breakpoint.M`
    width: 100%;
    margin-lef: auto;
    margin-right: auto;
    justify-content: space-between;
    flex-direction: ${({ alignment }) =>
      alignment === Alignments.LEFT ? 'row' : 'row-reverse'};
    gap: ${Spacings.XXXL};
  `}
`;

const StyledH2 = styled(H2)`
  margin: 0 0 ${Spacings.XS} 0;
`;

const TextContainer = styled.div`
  box-sizing: border-box;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  margin-top: ${Spacings.M};

  ${mq.from.breakpoint.M`
    margin-top: 0;
  `}
`;

const VisualContainer = styled.div`
  width: 100%;

  > video {
    max-height: 80vh;
    width: 100%;
  }

  ${mq.from.breakpoint.M`
    width: 50%;
  `}
`;

const StyledPrimaryButton = styled(PrimaryButton)`
  ${buttonStyles.primaryButtonPosition}
`;

const StyledSecondaryButton = styled(SecondaryButton)`
  ${buttonStyles.secondaryButtonPosition}
`;
